/* ──────────────────────────────────────────────────────────────────
   LSL | Accessibility settings panel (2026-10-09, Alex)
   Needs window.lslA11y from snippets/lsl-a11y-head (prefs + flip logic).

   Higher contrast: besides the CSS in assets/lsl-a11y.css, this walks
   the page and measures every line of text against the background it
   actually sits on. Anything under WCAG AA (4.5:1, or 3:1 for large
   text) gets the nearest palette colour that passes:
     dark text on a light background  -> RoHaus Red #a94b46 for warm
                                         reds, else RoHaus Text #3c2f2f
     light text on a dark background  -> RoHaus White #fff9f1, and the
                                         background itself steps down to
                                         #a94b46 / #3c2f2f if needed
   Text that measures under 1.4:1 is almost always sitting on a photo the
   maths can't see, so it is left alone. Every change is undone when the
   switch goes off.
   ────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  var A = window.lslA11y;
  if (!A) return;
  var d = document.documentElement;
  var p = A.prefs;
  var panel = document.getElementById('lsl-a11y-panel');
  var opener = null;

  /* ── colour maths ── */
  function parse(c) {
    if (!c) return null;
    var m = c.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,\/]\s*([\d.]+)(%?))?\s*\)$/);
    if (m) return [+m[1], +m[2], +m[3], m[4] === undefined ? 1 : (m[5] ? m[4] / 100 : +m[4])];
    m = c.match(/^color\(srgb\s+([\d.e-]+)\s+([\d.e-]+)\s+([\d.e-]+)(?:\s*\/\s*([\d.]+)(%?))?\s*\)$/);
    if (m) return [m[1] * 255, m[2] * 255, m[3] * 255, m[4] === undefined ? 1 : (m[5] ? m[4] / 100 : +m[4])];
    return null;
  }
  function channel(v) { v /= 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
  function lum(c) { return 0.2126 * channel(c[0]) + 0.7152 * channel(c[1]) + 0.0722 * channel(c[2]); }
  function ratio(a, b) { var x = lum(a), y = lum(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); }
  function over(top, under) {
    var a = top[3];
    return [top[0] * a + under[0] * (1 - a), top[1] * a + under[1] * (1 - a), top[2] * a + under[2] * (1 - a), 1];
  }
  function warm(c) { return c[0] > c[1] + 25 && c[0] > c[2] + 25; }
  function css(c) { return 'rgb(' + Math.round(c[0]) + ', ' + Math.round(c[1]) + ', ' + Math.round(c[2]) + ')'; }
  var PAGE = [246, 232, 220, 1]; /* RoHaus Background */
  var DARK = [60, 47, 47, 1];    /* RoHaus Text */
  var CREAM = [255, 249, 241, 1];/* RoHaus White */
  var RED = [169, 75, 70, 1];    /* RoHaus Red */

  /* nearest solid background behind an element: { c, owner } or null when
     an image or gradient sits behind it */
  function bgOf(el, memo) {
    if (!el || el.nodeType !== 1) return { c: PAGE, owner: null };
    if (memo.has(el)) return memo.get(el);
    var cs = getComputedStyle(el);
    var res;
    if (cs.backgroundImage && cs.backgroundImage !== 'none') {
      res = null;
    } else {
      var c = parse(cs.backgroundColor);
      if (c && c[3] >= 0.98) {
        res = { c: c, owner: el };
      } else {
        var up = bgOf(el.parentElement, memo);
        res = !up ? null : (c && c[3] > 0 ? { c: over(c, up.c), owner: up.owner } : up);
      }
    }
    memo.set(el, res);
    return res;
  }

  /* ── inline overrides we can undo ── */
  var touched = new Set();
  /* 'background-color' -> dataset.lslHcBackgroundColor (data-lsl-hc-background-color) */
  function key(prop) {
    return 'lslHc' + prop.replace(/(^|-)(.)/g, function (_, dash, l) { return l.toUpperCase(); });
  }
  function setProp(el, prop, val) {
    var k = key(prop);
    if (!(k in el.dataset)) el.dataset[k] = el.style.getPropertyValue(prop) + '|' + el.style.getPropertyPriority(prop);
    el.style.setProperty(prop, val, 'important');
    touched.add(el);
  }
  function restoreAll() {
    touched.forEach(function (el) {
      ['color', 'background-color', 'opacity'].forEach(function (prop) {
        var k = key(prop);
        if (!(k in el.dataset)) return;
        var saved = el.dataset[k].split('|');
        if (saved[0]) el.style.setProperty(prop, saved[0], saved[1] || '');
        else el.style.removeProperty(prop);
        delete el.dataset[k];
      });
    });
    touched.clear();
  }

  /* opacity the element inherits from itself and every ancestor */
  function fadeOf(el, memo) {
    if (!el || el.nodeType !== 1 || el === document.body) return 1;
    if (memo.has(el)) return memo.get(el);
    var o = parseFloat(getComputedStyle(el).opacity);
    var f = (isNaN(o) ? 1 : o) * fadeOf(el.parentElement, memo);
    memo.set(el, f);
    return f;
  }

  function isLarge(cs) {
    var size = parseFloat(cs.fontSize);
    var weight = parseInt(cs.fontWeight, 10) || 400;
    return size >= 24 || (size >= 18.66 && weight >= 700);
  }

  var applying = false;
  function contrastPass() {
    if (!d.classList.contains('lsl-contrast')) return;
    applying = true;
    var memo = new Map();
    var fades = new Map();
    var seen = new Set();
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
    var n;
    while ((n = walker.nextNode())) {
      if (!n.nodeValue || !n.nodeValue.trim()) continue;
      var el = n.parentElement;
      if (!el || seen.has(el)) continue;
      seen.add(el);
      if (el.closest('script, style, noscript, template, svg, #lsl-a11y-panel')) continue;
      var cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;
      if (!el.getClientRects().length) continue;
      var fg = parse(cs.color);
      if (!fg) continue;
      var own = parseFloat(cs.opacity);
      var fade = fadeOf(el, fades);
      if (!(own > 0) || fade < 0.1) continue; /* hidden or mid-reveal */
      var bg = bgOf(el, memo);
      if (!bg) continue;
      var shown = over([fg[0], fg[1], fg[2], fg[3] * Math.min(fade, 1)], bg.c);
      var need = isLarge(cs) ? 3 : 4.5;
      var r = ratio(shown, bg.c);
      if (r >= need || r < 1.4) continue;
      var bgDark = lum(bg.c) < 0.3;
      if (own < 1) setProp(el, 'opacity', '1');
      if (!bgDark) {
        setProp(el, 'color', css(warm(fg) && ratio(RED, bg.c) >= need ? RED : DARK));
      } else if (ratio(CREAM, bg.c) >= need) {
        setProp(el, 'color', css(CREAM));
      } else if (bg.owner && bg.owner !== document.body && bg.owner !== d) {
        setProp(bg.owner, 'background-color', css(warm(bg.c) ? RED : DARK));
        setProp(el, 'color', css(CREAM));
        memo = new Map();
      }
    }
    applying = false;
    if (observer) observer.takeRecords();
  }

  var observer = null;
  var passTimer = null;
  function queuePass() {
    clearTimeout(passTimer);
    passTimer = setTimeout(contrastPass, 400);
  }
  function startContrast() {
    contrastPass();
    if (!observer && window.MutationObserver) {
      observer = new MutationObserver(function () { if (!applying) queuePass(); });
    }
    if (observer) observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'open'] });
    window.addEventListener('resize', queuePass);
  }
  function stopContrast() {
    if (observer) observer.disconnect();
    window.removeEventListener('resize', queuePass);
    clearTimeout(passTimer);
    restoreAll();
  }

  /* ── movement: muted videos that play on their own ── */
  function stopMedia() {
    document.querySelectorAll('video').forEach(function (v) {
      if (!(v.muted || v.hasAttribute('muted'))) return; /* a video someone pressed play on with sound stays theirs */
      if (v.autoplay || !v.paused) {
        v.dataset.lslPaused = '1';
        v.autoplay = false;
        v.pause();
      }
    });
    /* the typing letter: finish it in one go */
    document.querySelectorAll('[data-letter-skip]').forEach(function (b) {
      if (b.offsetParent) b.click();
    });
  }
  /* lazy videos start themselves when they scroll into view */
  document.addEventListener('play', function (e) {
    var v = e.target;
    if (!v || v.tagName !== 'VIDEO' || !d.classList.contains('lsl-motion-off')) return;
    if (!(v.muted || v.hasAttribute('muted')) || v.controls) return;
    /* a play someone started on purpose with a video's own pause/play button stays theirs */
    if (v.dataset.userPlay === '1') return;
    /* the About story video unmutes before it plays, so its sound button still works */
    v.dataset.lslPaused = '1';
    v.pause();
  }, true);
  function resumeMedia() {
    document.querySelectorAll('video[data-lsl-paused]').forEach(function (v) {
      delete v.dataset.lslPaused;
      if (v.dataset.userPaused) return; /* someone paused it themselves: leave it paused */
      v.autoplay = true;
      var playing = v.play();
      if (playing && playing.catch) playing.catch(function () {});
    });
  }

  function afterApply() {
    if (d.classList.contains('lsl-contrast')) startContrast();
    else stopContrast();
    if (d.classList.contains('lsl-motion-off')) stopMedia();
    else resumeMedia();
  }

  /* ── panel ── */
  function setSwitch(name, on) {
    if (!panel) return;
    var b = panel.querySelector('[data-lsl-a11y-toggle="' + name + '"]');
    if (!b) return;
    b.setAttribute('aria-checked', on ? 'true' : 'false');
    var s = b.querySelector('.lsl-a11y__state');
    if (s) s.textContent = on ? 'on' : 'off';
  }
  function syncUI() {
    if (!panel) return;
    var t = p.text || 'md';
    panel.querySelectorAll('input[name="lsl-a11y-text"]').forEach(function (i) { i.checked = i.value === t; });
    setSwitch('contrast', !!p.contrast);
    setSwitch('motion', A.forced() === 'reduce');
    var note = panel.querySelector('[data-lsl-a11y-os-note]');
    if (note) note.hidden = !(A.osReduce && p.motion !== 'on');
  }
  function announce(msg) {
    var s = panel && panel.querySelector('[data-lsl-a11y-status]');
    if (!s) return;
    s.textContent = '';
    setTimeout(function () { s.textContent = msg; }, 50);
  }
  function commit(msg) {
    A.save();
    A.apply();
    afterApply();
    syncUI();
    if (msg) announce(msg);
  }

  function open(e) {
    if (e) e.preventDefault();
    if (!panel) return;
    opener = document.activeElement;
    syncUI();
    if (typeof panel.showModal === 'function') {
      if (!panel.open) panel.showModal();
    } else {
      panel.setAttribute('open', '');
    }
  }

  if (panel) {
    panel.addEventListener('change', function (e) {
      if (e.target.name !== 'lsl-a11y-text') return;
      if (e.target.value === 'md') delete p.text;
      else p.text = e.target.value;
      commit();
    });
    panel.addEventListener('click', function (e) {
      var sw = e.target.closest('[data-lsl-a11y-toggle]');
      if (sw) {
        var name = sw.getAttribute('data-lsl-a11y-toggle');
        var on = sw.getAttribute('aria-checked') !== 'true';
        if (name === 'contrast') {
          if (on) p.contrast = true; else delete p.contrast;
        } else if (name === 'motion') {
          p.motion = on ? 'off' : 'on';
          if (!on && !A.osReduce) delete p.motion;
        }
        commit();
        return;
      }
      if (e.target.closest('[data-lsl-a11y-reset]')) {
        Object.keys(p).forEach(function (k) { delete p[k]; });
        commit('Settings reset to default.');
        return;
      }
      /* a click on the dimmed backdrop closes the panel */
      if (e.target === panel) {
        var r = panel.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) panel.close();
      }
    });
    panel.addEventListener('close', function () {
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
      if (location.hash === '#accessibility-settings' && history.replaceState) {
        history.replaceState(null, '', location.pathname + location.search);
      }
    });
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-lsl-a11y-open], a[href$="#accessibility-settings"]');
    if (t) open(e);
  });
  window.addEventListener('hashchange', function () {
    if (location.hash === '#accessibility-settings') open();
  });
  if (location.hash === '#accessibility-settings') open();

  /* saved choices that need the DOM */
  function boot() { afterApply(); }
  if (document.readyState === 'complete') boot();
  else window.addEventListener('load', boot);
})();
